import _ from "lodash";
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { reduxForm, Field, initialize, Form } from "redux-form";
import { ReactSortable } from "react-sortablejs";
import { Table, Icon, Button } from "semantic-ui-react";
import {
  CheckboxInput,
  TextInput,
  RadioInput,
} from "../../templates/utils/form-fields";
import { createRetailer } from "../../firebase";
import Loading from "../../app/loading";

let GamTemplate = ({
  retailer,
  data,
  id,
  submitting,
  pristine,
  handleSubmit,
  initialized,
}) => {
  const dispatch = useDispatch();

  const [variables, setVariables] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [showLoading, setShowLoading] = useState(false);
  const [isPristine, setIsPristine] = useState(true);
  const [pickerOptions, setPickerOptions] = useState([]);

  const merge = async (gam, ret) => {
    let fields = [];
    if (!ret) {
      fields = _.map(gam, (item, index) => {
        return { id: index, ...item };
      });
    } else {
      ret.forEach((item) => {
        let i = _.findIndex(gam, (o) => {
          return item.uniqueName === o.uniqueName;
        });
        if (i !== -1) fields.push(item);
      });
      gam.forEach((item) => {
        let i = _.findIndex(fields, (o) => {
          return item.uniqueName === o.uniqueName;
        });
        if (i === -1) fields.push({ ...item, id: fields.length - 1 });
      });
    }
    let result = await fields;
    return result;
  };

  useEffect(() => {
    if (data) {
      merge(data.result[0].variables, retailer.templates[id].variables).then(
        (fields) => {
          setVariables(fields);
        }
      );
    }
    return () => {
      setVariables([]);
    };
  }, [data, id, retailer]);

  useEffect(() => {
    if (data || id) {
      const formData = retailer.templates[id] || data.result[0];
      setFormValues(formData);
    }
  }, [data, retailer, id]);

  useEffect(() => {
    if (variables.length > 0 && !initialized)
      dispatch(initialize("templateForm", formValues));
  }, [variables, dispatch, initialized, formValues]);

  useEffect(() => {
    setIsPristine(pristine);
  }, [pristine]);

  useEffect(() => {
    const options = variables.map((item) => item.colorPicker || false);
    setPickerOptions(options);
  }, [variables]);

  const orderCallback = useCallback((data) => {
    setVariables(data);
    if (data.length > 0) setIsPristine(false);
  }, []);

  const updateVariables = (update, index) => {
    setVariables((prevState) =>
      prevState.map((value, i) => {
        return i === index ? { ...value, ...update } : { ...value };
      })
    );
  };

  const handleChange = (item, index, event) => {
    let val;
    if (item === "colorPicker") {
      val =
        !event.target.value || event.target.value === "false"
          ? { [item]: true }
          : { [item]: false };
      let newOptions = [...pickerOptions];
      newOptions[index] = val;
      setPickerOptions(newOptions);
    } else {
      val = { [item]: event.target.value };
    }
    updateVariables(val, index);
  };

  const handleSubmitForm = (values) => {
    setShowLoading(true);
    const update = { ...retailer };
    update.templates[id].variables = variables;
    createRetailer(update).then(() => {
      setShowLoading(false);
    });
  };

  return (
    <Form onSubmit={handleSubmit(handleSubmitForm)} className="ui form">
      {showLoading && <Loading />}
      <Table basic="very" celled collapsing unstackable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Label</Table.HeaderCell>
            <Table.HeaderCell>Name</Table.HeaderCell>
            <Table.HeaderCell>Required</Table.HeaderCell>
            <Table.HeaderCell>Color picker</Table.HeaderCell>
            <Table.HeaderCell>Max chars</Table.HeaderCell>
            <Table.HeaderCell>Image size</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <ReactSortable list={variables} setList={orderCallback} tag="tbody">
          {variables.map((item, index) => (
            <Table.Row key={`${item.id}-${index}`}>
              <Table.Cell>{item.label}</Table.Cell>
              <Table.Cell>{item.uniqueName}</Table.Cell>
              <Table.Cell>
                {item.isRequired && <Icon color="green" name="check" />}
              </Table.Cell>
              <Table.Cell>
                {item.attributes["xsi:type"] ===
                  "StringCreativeTemplateVariable" && (
                  <>
                    <Field
                      component={CheckboxInput}
                      label={" "}
                      name={`variables[${index}].colorPicker`}
                      type="checkbox"
                      onChange={(event) =>
                        handleChange("colorPicker", index, event)
                      }
                    />
                    {pickerOptions[index] && (
                      <>
                        <Field
                          component={RadioInput}
                          label={"#Hex"}
                          name={`variables[${index}].colorPickerType`}
                          radioValue="hex"
                          onChange={(event) =>
                            handleChange("colorPickerType", index, event)
                          }
                        />
                        <Field
                          component={RadioInput}
                          label={"RGB"}
                          name={`variables[${index}].colorPickerType`}
                          radioValue="rgb"
                          onChange={(event) =>
                            handleChange("colorPickerType", index, event)
                          }
                        />
                        <Field
                          component={RadioInput}
                          label={"Hex"}
                          name={`variables[${index}].colorPickerType`}
                          radioValue="nohash"
                          onChange={(event) =>
                            handleChange("colorPickerType", index, event)
                          }
                        />
                      </>
                    )}
                  </>
                )}
              </Table.Cell>
              <Table.Cell>
                {item.attributes["xsi:type"] ===
                  "StringCreativeTemplateVariable" && (
                  <Field
                    component={TextInput}
                    label={" "}
                    name={`variables[${index}].max`}
                    type="number"
                    onChange={(event) => handleChange("max", index, event)}
                  />
                )}
              </Table.Cell>
              <Table.Cell>
                {item.attributes["xsi:type"] ===
                  "AssetCreativeTemplateVariable" && (
                  <Field
                    component={TextInput}
                    label={" "}
                    name={`variables[${index}].size`}
                    type="text"
                    onChange={(event) => handleChange("size", index, event)}
                  />
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </ReactSortable>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan="5">
              <Button
                disabled={submitting || isPristine}
                positive
                type="submit"
                size="mini"
              >
                Save
              </Button>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </Form>
  );
};

GamTemplate = reduxForm({
  form: "templateForm",
})(GamTemplate);

export default GamTemplate;
